import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router } from '@angular/router';
import { UserLoginService } from '../service/user-login.service';
import { AuthableRole, UserProfile } from '../types';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CanActivateResponse } from '../types/guard';

@Injectable({
  providedIn: 'root',
})
export class ActiveUserGuard implements CanActivate, CanActivateChild {
  private userService = inject(UserLoginService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): Observable<CanActivateResponse> | CanActivateResponse {
    const user = this.userService.currentProfile();
    if (!user) {
      return this.userService.currentProfileLoaded$.pipe(
        map((user) => {
          return this.evaluate(route, user);
        }),
      );
    }

    return this.evaluate(route, user);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<CanActivateResponse> | CanActivateResponse {
    return this.canActivate(childRoute);
  }

  evaluate(route: ActivatedRouteSnapshot, user: UserProfile): CanActivateResponse {
    const active = user.account_status === 'Active';

    const allowedIfInactive = route.data.allowedIfInactive ?? false;
    const allowedIfInactiveRoles = (route.data.allowedIfInactiveRoles ?? []) as AuthableRole[];
    const allowedIfSuspendedRoles = (route.data.allowedIfSuspendedRoles ?? []) as AuthableRole[];
    const hasAllowedInactiveRole =
      allowedIfInactiveRoles.length === 0 ||
      allowedIfInactiveRoles.some((role) => this.userService.currentProfileRoleList().includes(role));
    const hasAllowedSuspendedRole =
      allowedIfSuspendedRoles.length === 0 ||
      allowedIfSuspendedRoles.some((role) => this.userService.currentProfileRoleList().includes(role));

    if (user.access_suspended) {
      return route.data.allowedIfSuspended && hasAllowedSuspendedRole ? true : this.router.createUrlTree(['home']);
    }

    if (active || (allowedIfInactive && hasAllowedInactiveRole)) {
      return true;
    }
    return this.router.createUrlTree(['home']);
  }
}
